import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * Keeps one broken panel from blanking the whole studio. Users are mid-build when something breaks —
 * losing the entire screen (and the sense that their work survived) is the worst possible outcome.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // eslint-disable-next-line no-console
    console.error('[studio] render error:', error.message, info.componentStack);
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <div className="flex h-full items-center justify-center p-6">
        <div className="max-w-md space-y-3 text-center">
          <h1 className="text-base font-semibold">This screen ran into a problem</h1>
          <p className="text-sm text-muted">
            Your project is safe — nothing was lost. Reload to pick up where you left off.
          </p>
          <p className="rounded-lg border border-line bg-page p-2 font-mono text-xs text-muted">
            {error.message}
          </p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="rounded-lg bg-accent px-4 py-2 text-sm font-medium text-accent-ink transition-opacity hover:opacity-90"
          >
            Reload
          </button>
        </div>
      </div>
    );
  }
}
